/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1780358400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jun 02 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "linkedin",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to LinkedIn Ads",
          "depth": 0,
          "id": "connect-to-linkedin-ads"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences"
        }, {
          "value": "Syncing conversions",
          "depth": 1,
          "id": "syncing-conversions"
        }, {
          "value": "Handling PII and hashing",
          "depth": 0,
          "id": "handling-pii-and-hashing"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img"
    }, _provideComponents(), props.components), {ArrayFanOutTip, EventSyncWarning, MatchRatesExplanation, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Deliver even more relevant ads by combining data from various sources within your\ndata warehouse to build custom audiences for LinkedIn Ads. By keeping your\ncustom audiences updated automatically, you can avoid showing irrelevant ads."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Create new audience to sync users or companies"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://learn.microsoft.com/en-us/linkedin/marketing/integrations/matched-audiences/matched-audiences?view=li-lms-2023-11",
                children: "Audiences docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversions",
                children: _jsx(_components.strong, {
                  children: "Conversions"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to LinkedIn conversions"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://learn.microsoft.com/en-us/linkedin/marketing/integrations/ads-reporting/conversions-api?view=li-lms-2023-11&tabs=http",
                children: "Conversions docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-linkedin-ads",
        children: "Connect to LinkedIn Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), "\nand click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "LinkedIn Ads"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nYou can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "LinkedIn Ads"
        }), " using OAuth."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to LinkedIn Ads"
        }), " and log into\nyour LinkedIn Ads account."]
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name\nfor your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your LinkedIn Ads destination and have a\n", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from,\nyou can set up your sync configuration to begin syncing data. Go to the\n", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the\n", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the LinkedIn\nAds destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences",
        children: "Syncing audiences"
      }), "\n", _jsx(_components.p, {
        children: "To get started, select an ad account to sync your data to, then select\nwhich audience object you want to sync data to: Company or User."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports automatically creating a new audience for your sync. You can\nspecify a custom name for this audience, or Hightouch will use the name of the\nmodel."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "After the initial run of the sync, you can't update the ad account, audience\nobject, or name of the audience. To make these changes, create a new sync."
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Upsert mode. New users will be inserted into the LinkedIn\naudience. Users that are removed from the model will be removed from the\naudience."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration has two field mapping sections:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "ID fields"
          }), ": These are the primary identity keys that LinkedIn uses to match users, such as hashed email or Google Advertising ID (GAID). These values become ", _jsx(_components.code, {
            children: "userIds"
          }), " in the LinkedIn API payload. You must map at least one ID field."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Additional fields"
          }), ": These are supplemental fields—such as first name, last name, title, company, and country—that improve LinkedIn's match rate but aren't primary identifiers."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "LinkedIn requires emails to be hashed using SHA256 or SHA512. Hightouch handles this automatically on the backend."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-linkedin-field-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "LinkedIn"
      }), "\n", _jsxs(Warning, {
        children: [_jsxs(_components.p, {
          children: ["LinkedIn enforces the following input data requirements: - All IDs provided by\n", _jsx(_components.code, {
            children: "userId"
          }), " or ", _jsx(_components.code, {
            children: "userIds"
          }), " fields must have a ", _jsx(_components.a, {
            href: "https://learn.microsoft.com/en-us/linkedin/marketing/integrations/matched-audiences/create-and-manage-segment-users?view=li-lms-unversioned&tabs=http#input-data-requirements",
            children: "supported type and valid\nvalue"
          }), "."]
        }), _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: ["Each row of data must provide: (1) at least one valid ID; or (2) A valid\n", _jsx(_components.code, {
              children: "firstName"
            }), " and ", _jsx(_components.code, {
              children: "lastName"
            }), ". See LinkedIn's ", _jsx(_components.a, {
              href: "https://learn.microsoft.com/en-us/linkedin/marketing/integrations/matched-audiences/create-and-manage-segment-users?view=li-lms-unversioned&tabs=http#schema",
              children: "user data\nschema"
            }), "\nfor more information."]
          }), "\n"]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversions",
        children: "Syncing conversions"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to LinkedIn as conversion events."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["Create a ", _jsx(_components.a, {
          href: "https://www.linkedin.com/help/lms/answer/a1657171",
          children: "conversion rule"
        }), " in LinkedIn Campaign Manager."]
      }), "\n", _jsx(_components.p, {
        children: "Select \"Conversions\" in your sync in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-linkedin-conversions-type.png",
          alt: "LinkedIn Conversions sync"
        })
      }), "\n", _jsx(_components.p, {
        children: "Select your LinkedIn account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-linkedin-conversions-account-select.png",
          alt: "LinkedIn Conversions account"
        })
      }), "\n", _jsx(_components.p, {
        children: "Choose your event timestamp or let LinkedIn use the timestamp the event arrives at the server."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-linkedin-conversions-timestamp.png",
          alt: "LinkedIn Conversions event timestamp"
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to conversions in LinkedIn on the following user fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email (SHA256)"
        }), "\n", _jsx(_components.li, {
          children: "LinkedIn tracking UUID"
        }), "\n", _jsx(_components.li, {
          children: "Acxiom ID"
        }), "\n", _jsx(_components.li, {
          children: "Oracle moat ID"
        }), "\n", _jsx(_components.li, {
          children: "IP address (IPv4)"
        }), "\n", _jsx(_components.li, {
          children: "Google Advertising ID (GAID)"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["You can enable PII hashing if you want Hightouch to automatically detect, normalize, and hash fields that require it. For example, if you map to a ", _jsx(_components.code, {
          children: "Email (SHA256)"
        }), " field and the model column with the email data is unhashed, Hightouch hashes the value for you."]
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically detects if the following fields require hashing:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email (SHA256)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email (SHA512)"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "view-conversion-tracking-and-reporting-metrics",
        children: "View conversion tracking and reporting metrics"
      }), "\n", _jsx(_components.p, {
        children: "Once your sync is set up and published, you can view conversions tracking and reporting metrics in your LinkedIn Campaign Manager."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to LinkedIn, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#linkedin",
            children: "Error codes: LinkedIn"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the audiences sync type."
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "LinkedIn Ads"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "400---missing_required_field",
        children: "400 - MISSING_REQUIRED_FIELD"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive a ", _jsx(_components.code, {
          children: "400 - type=MISSING_REQUIRED_FIELD"
        }), " because the sync is sending a required field ", _jsx(_components.code, {
          children: "null"
        }), " values."]
      }), "\n", _jsx(_components.p, {
        children: "You can resolve this error by either:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.a, {
            href: "/models/sql-editor#exclude-rows-with-null-values",
            children: ["Removing rows containing ", _jsx(_components.code, {
              children: "null"
            }), " values"]
          }), " from your model's query results"]
        }), "\n", _jsxs(_components.li, {
          children: ["Replacing any ", _jsx(_components.code, {
            children: "null"
          }), " values in your model's query results with another value"]
        }), "\n", _jsxs(_components.li, {
          children: ["Using the ", _jsx(_components.a, {
            href: "/syncs/mapping-data#advanced-mapper",
            children: "advanced mapper"
          }), " to ", _jsxs(_components.a, {
            href: "/syncs/mapping-data#dont-sync-null-values",
            children: ["ignore any ", _jsx(_components.code, {
              children: "null"
            }), " values"]
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-column-mapping-ignore-null.png",
          alt: "Column Mapping with ignore null option enabled"
        })
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
